import { inputClass, textareaClass } from "../ui";
import type { FormFieldDefinition, FormValue } from "../../types";

export interface FormFieldProps {
  field: FormFieldDefinition;
  value: FormValue;
  onChange: (value: FormValue) => void;
}

const numericTypes = ["number", "rating"];

export function FormField({ field, value, onChange }: FormFieldProps) {
  const options = field.options || [];

  if (field.type === "informational") {
    return (
      <p className="rounded-md bg-app-field p-3 text-sm leading-relaxed text-app-ink">
        {field.label}
      </p>
    );
  }

  if (field.type === "dropdown" || field.type === "single_choice") {
    return (
      <label className="grid gap-1.5 text-sm font-semibold text-app-ink">
        {field.label}
        {field.help_text && (
          <span className="text-xs font-normal text-app-copy">{field.help_text}</span>
        )}
        <select
          className={inputClass}
          required={field.required}
          value={(value as string | undefined) || ""}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Select</option>
          {options.map((option) => (
            <option key={option.id} value={option.text}>
              {option.text}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === "checklist") {
    const selected = (value as string[] | undefined) || [];
    return (
      <fieldset className="rounded-md border border-app-line p-4">
        <legend className="px-1 text-sm font-semibold text-app-ink">{field.label}</legend>
        {options.map((option) => (
          <label
            className="my-2.5 flex min-h-10 items-center gap-2 text-sm font-medium text-app-ink"
            key={option.id}
          >
            <input
              className="h-5 w-5 accent-accent"
              type="checkbox"
              checked={selected.includes(option.text)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...selected, option.text]
                    : selected.filter((entry) => entry !== option.text)
                )
              }
            />
            {option.text}
          </label>
        ))}
      </fieldset>
    );
  }

  if (field.type === "long_text") {
    return (
      <label className="grid gap-1.5 text-sm font-semibold text-app-ink">
        {field.label}
        <textarea
          className={textareaClass}
          required={field.required}
          value={(value as string | undefined) || ""}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
    );
  }

  if (field.type === "yes_no") {
    return (
      <label className="grid gap-1.5 text-sm font-semibold text-app-ink">
        {field.label}
        <select
          className={inputClass}
          required={field.required}
          value={(value as string | undefined) || ""}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Select</option>
          <option>Yes</option>
          <option>No</option>
        </select>
      </label>
    );
  }

  return (
    <label className="grid gap-1.5 text-sm font-semibold text-app-ink">
      {field.label}
      <input
        className={inputClass}
        required={field.required}
        type={
          field.type === "date" ? "date" : numericTypes.includes(field.type) ? "number" : "text"
        }
        value={(value as string | number | undefined) ?? ""}
        onChange={(event) =>
          onChange(
            numericTypes.includes(field.type) ? Number(event.target.value) : event.target.value
          )
        }
      />
    </label>
  );
}
